import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { PageHeading } from '@components/layout/Heading'
import { HeadTitle } from '@components/layout/HeadTitle'
import PageContent from '@components/layout/PageContent'
import { Card, Flex } from '@clear/design-system'
import Spinner from '@components/shared/Spinner'
import { useRouter } from 'next/router'
import { Actions, Form, ErrorMessage } from '@components/layout/Form'
import { ContinueButton } from '@components/FormButtons'
import { biographicSearchSchema } from '@validators/schema/shared/biographicSearchSchema'
import { setFormErrors, extractErrors } from '@utils/apiErrorMessages'
import useGetLocales from '@hooks/useGetLocales'
import BiographicsStatusSearchView from '@components/pages/shared/BiographicsForm'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import useEnrollmentApi from '@hooks/useEnrollmentApi'
import { useSessionTimer } from '@hooks/useSessionTimer'
import { getXCLEARCorrelationInfo } from '@clear-denver/tpc-openapi-typescript/build/utils'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'

const biographicSearchDefaultValues: Partial<Enrollment.BiographicsLookup> = {
  firstName: '',
  noMiddleName: false,
  middleName: '',
  lastName: '',
  countryOfBirth: '',
}

type LookUpFormType = {
  headTitle?: string
  headDescription?: string
  title?: string
  showSuffix?: boolean
  onSubmitByBiographic: (
    formData: Enrollment.BiographicsLookup
  ) => Promise<Enrollment.ApplicantStatusDetails | Enrollment.KTNDetails | void>
  success?: (props: any) => JSX.Element
}

function LookUpForm({
  headTitle,
  headDescription,
  title,
  showSuffix,
  onSubmitByBiographic,
}: LookUpFormType) {
  const {
    register: biographicSearchRegister,
    handleSubmit: biographicSearchHandleSubmit,
    formState: biographicSearchFormState,
    watch: biographicSearchFormWatch,
    setError: biographicSearchSetErrors,
    clearErrors: biographicSearchClearErrors,
    control: biographicSearchControl,
    setValue: biographicSearchSetValue,
  } = useForm<Enrollment.BiographicsLookup & { global: string }>({
    mode: 'onChange',
    resolver: yupResolver(biographicSearchSchema),
    defaultValues: biographicSearchDefaultValues,
  })

  const { errors: biographicSearchErrors } = biographicSearchFormState

  const [countries] = useGetLocales()
  const [isLoading, setIsLoading] = useState(false)

  const { clearState: clearEnrollmentState } = useEnrollmentApi()
  const { sessionTimeoutHandler } = useSessionTimer(clearEnrollmentState)
  const router = useRouter()

  const onSubmitBiographicForm = async (
    formData: Enrollment.BiographicsLookup
  ) => {
    biographicSearchClearErrors('global')
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'Submit by biographics begun',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.SUBMIT_APPLICATION_BY_BIOGRAPHICS_BEGIN,
      },
    })
    try {
      await onSubmitByBiographic(formData)
      dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'Submit by biographics successful',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.SUBMIT_APPLICATION_BY_BIOGRAPHICS_SUCCESSFUL,
        },
      })
    } catch (error) {
      if (error.status === 401) {
        dispatchCustomEvent(Enrollment.LogEntryLevelEnum.ERROR, {
          payload: {
            message: error?.message,
            stack: error?.stack,
          },
          metadata: {
            event: DiscreteEvent.SUBMIT_APPLICATION_BY_BIOGRAPHICS_FAILURE,
            ...baseMetaData,
          },
        })
        sessionTimeoutHandler()
      }
      setFormErrors(
        await extractErrors(error, formData),
        biographicSearchSetErrors
      )
    }

    setIsLoading(false)
  }

  const biographicValues = biographicSearchFormWatch()
  const hasNotSuccessfullyEnteredBiographicData =
    !biographicSearchFormState.isDirty || !biographicSearchFormState.isValid

  return (
    <PageContent>
      <HeadTitle title={headTitle} description={headDescription} />
      <PageHeading>{title}</PageHeading>
      <Card width="full">
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          minHeight="30.25rem"
        >
          <Form width="full">
            <BiographicsStatusSearchView
              control={biographicSearchControl}
              register={biographicSearchRegister}
              setValue={biographicSearchSetValue}
              countries={countries}
              showSuffix={showSuffix}
              noMiddleName={biographicValues.noMiddleName}
              firstNameErrorMessage={biographicSearchErrors.firstName?.message}
              middleNameErrorMessage={
                biographicSearchErrors.middleName?.message
              }
              lastNameErrorMessage={biographicSearchErrors.lastName?.message}
              dobErrorMessage={biographicSearchErrors.dob?.message}
              countryOfBirthErrorMessage={
                biographicSearchErrors.countryOfBirth?.message
              }
            />
          </Form>
          <Actions
            justifyContent="flex-end"
            alignSelf="flex-end"
            width="full"
            mt={[6, 0]}
            mb={6}
          >
            <ContinueButton
              disabled={hasNotSuccessfullyEnteredBiographicData}
              onClick={biographicSearchHandleSubmit(onSubmitBiographicForm)}
              data-cy="continue"
            />
          </Actions>
        </Flex>
        {biographicSearchErrors.global &&
          (process.env.NEXT_PUBLIC_APP_ENV === 'test' ||
            process.env.NODE_ENV === 'development') && (
            <ErrorMessage>{biographicSearchErrors.global.message}</ErrorMessage>
          )}
      </Card>
      {isLoading && <Spinner />}
    </PageContent>
  )
}

export default LookUpForm
